'use client';

import React, { useEffect, useRef, useState } from 'react';

interface FounderLetterProps {
  className?: string;
}

export function FounderLetter({ className = '' }: FounderLetterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const shareText =
    'Rekomendasi platform hukum Indonesia: Justisio. Fiturnya lengkap buat riset regulasi - search konteks, konsolidasi, dan tools legal lainnya. Cek di justisio.com';
  const shareUrl = 'https://justisio.com';
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl);

  const socialLinks = [
    { name: 'WhatsApp', url: `https://wa.me/?text=${encodedText}` },
    { name: 'Twitter/X', url: `https://twitter.com/intent/tweet?text=${encodedText}` },
    { name: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}` },
    { name: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
    { name: 'Telegram', url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}` },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className={`py-16 md:py-24 px-4 md:px-6 bg-neutral-50 ${className}`}>
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border-2 border-neutral-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
          <div className="bg-neutral-100 border-b border-neutral-200 px-4 py-2 flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 text-center">
              <span className="text-xs text-neutral-400">inbox - justisio.com</span>
            </div>
          </div>

          <div className="bg-neutral-700 text-white px-4 py-2 flex items-center justify-between text-sm">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input type="checkbox" className="w-3 h-3 rounded" defaultChecked readOnly />
                <svg className="w-4 h-4 text-neutral-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <a
                href="mailto:ali@justisio.com?subject=Ada masukan untuk Justisio"
                className="flex items-center gap-1 px-2 py-0.5 bg-neutral-600 hover:bg-neutral-500 rounded transition-colors text-xs"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                  />
                </svg>
                Reply
              </a>
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center gap-1 px-2 py-0.5 bg-neutral-600 hover:bg-neutral-500 rounded transition-colors text-xs"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                  Forward
                </button>
                {isOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-50 min-w-[140px]">
                    {socialLinks.map((social) => (
                      <a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-3 py-1.5 text-neutral-700 hover:bg-neutral-100 text-xs"
                      >
                        {social.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <span className="text-neutral-400 text-xs">12 Feb 2025, 09:41</span>
          </div>

          <div className="px-4 py-3 border-b border-neutral-100">
            <h1 className="text-lg md:text-xl font-semibold text-neutral-900 mb-3">
              Surat dari Founder: Kenapa Kami Membangun Justisio
            </h1>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xs shrink-0">
                A
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 text-sm">
                  <span className="font-semibold text-neutral-900">Ali & Averrous</span>
                  <span className="text-neutral-400">to</span>
                  <span className="text-neutral-700">Pemerhati Hukum</span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-8 py-6 md:py-8 text-neutral-700 space-y-4 text-sm md:text-base leading-relaxed">
            <p className="font-semibold text-lg text-neutral-900">Halo semuanya!</p>
            <p>Kami Founder dari Justisio.</p>
            <p>
              Hukum sering kali mendapat reputasi buruk, tapi seharusnya tidak begitu. Hukum itu
              penting. Ia menjaga ketertiban, melindungi hak, dan menjadi fondasi bagaimana bisnis
              dan masyarakat berjalan.
            </p>
            <p>
              Namun, seiring berjalannya waktu, cara kita mengakses dan memahami hukum menjadi
              sangat rumit. Aturan yang tidak terorganisir, bahasa yang kaku, perubahan yang tidak
              tersinkronisasi. Platform hukum yang ada malah membiarkan hal ini terjadi, membuat
              kita merasa bahwa riset hukum memang harus rumit.
            </p>
            <p>
              Kita semua tahu rasanya kebingungan mencari dokumen, pusing menyatukan pasal yang
              direvisi, atau takut ada kewajiban bisnis yang terlewat.
            </p>
            <p>
              Itulah mengapa kami membangun Justisio. Ini adalah hasil dari obsesi kami untuk
              merombak total bagaimana Anda berinteraksi dengan informasi hukum. Kami merapikannya,
              membuatnya saling terkait, dan menyajikannya agar mudah dipahami oleh siapa saja.
            </p>
            <p className="bg-amber-50 px-4 py-3 rounded-lg border-l-4 border-amber-400 font-medium text-neutral-800">
              Justisio adalah harapan kami untuk memberikan akses hukum untuk banyak orang
            </p>
            <p>Kami mengundang Anda untuk mencobanya.</p>

            <div className="pt-6 mt-6 border-t border-neutral-100">
              <p className="text-neutral-500 mb-1 text-sm">Warm regards,</p>
              <p className="font-semibold text-neutral-900">Ali & Averrous</p>
              <p className="text-neutral-500 text-sm">Founders, Justisio</p>
            </div>
          </div>

          <div className="px-4 py-3 bg-neutral-50 border-t border-neutral-100 flex items-center gap-3">
            <a
              href="mailto:ali@justisio.com?subject=Ada masukan untuk Justisio"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                />
              </svg>
              Reply
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
